import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { X, ChevronDown, ChevronRight, Home, Car, Plane, Users, Info, FileText } from 'lucide-react';
import { RiMenu3Line } from "react-icons/ri";

export function NavBarComponent() {
    const [isOpen, setIsOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const toggleSidebar = () => {
        setIsOpen(!isOpen);
        if (!isOpen) setServicesOpen(false);
    };

    const isActive = (path) => location.pathname === path;

    const isServiceRoute = () => {
        return [
            "/employee-transportation",
            "/corporate-car",
            "/airport-transfer",
        ].includes(location.pathname);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
    };

    const handleNavigate = (path) => {
        navigate(path);
        setIsOpen(false);
    };

    useEffect(() => {
        if (isOpen) document.body.classList.add('overflow-hidden');
        else document.body.classList.remove('overflow-hidden');
        return () => document.body.classList.remove('overflow-hidden');
    }, [isOpen]);

    return (
        <>
            <button
                onClick={toggleSidebar}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-200 border-2 border-blue-100 px-2.5"
            >
                <RiMenu3Line className="text-[20px] text-gray-700" />
            </button>

            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-80 z-40 transition-opacity duration-300"
                    onClick={handleBackdropClick}
                />
            )}

            <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex items-center justify-between py-4 px-5 border-b border-gray-200">
                    <Link to="/" className="cursor-pointer">
                        <img src="/Logo.png" width="100%" className="w-40 md:w-40 lg:w-52" />
                    </Link>
                    <button onClick={toggleSidebar} className="p-2 hover:bg-gray-100 rounded-xl border">
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <nav className="p-4">
                    <ul className="space-y-2">

                        {/* Home */}
                        <li>
                            <button
                                onClick={() => handleNavigate("/")}
                                className={`flex items-center px-4 py-3 w-full rounded-lg transition duration-200 ${isActive("/") ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-100 text-gray-700"
                                    }`}
                            >
                                <Home className="w-5 h-5 mr-3" />
                                <span>Home</span>
                            </button>
                        </li>

                        {/* Services */}
                        <li>
                            <button
                                onClick={() => setServicesOpen(!servicesOpen)}
                                className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition duration-200 group 
                                    ${isServiceRoute() ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-100 text-gray-700"}`}
                            >
                                <div className="flex items-center">
                                    <Car className={`w-5 h-5 mr-3 ${isServiceRoute() ? "text-blue-700" : "text-gray-500 group-hover:text-blue-600"}`} />
                                    <span className={`font-medium ${isServiceRoute() ? "text-blue-700" : "group-hover:text-blue-600"}`}>Services</span>
                                </div>
                                {servicesOpen ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                )}
                            </button>

                            <div className={`overflow-hidden transition-all duration-300 ${servicesOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                                <ul className="ml-4 mt-2 space-y-1 border-l-2 border-gray-200 pl-4">
                                    <li>
                                        <button
                                            onClick={() => handleNavigate("/employee-transportation")}
                                            className={`flex items-center px-3 py-2 text-sm w-full rounded-md transition ${isActive("/employee-transportation") ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-50 text-gray-600"
                                                }`}
                                        >
                                            <Users className="w-4 h-4 mr-2" />
                                            <span>Employee Transportation</span>
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => handleNavigate("/corporate-car")}
                                            className={`flex items-center px-3 py-2 text-sm w-full rounded-md transition ${isActive("/corporate-car") ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-50 text-gray-600"
                                                }`}
                                        >
                                            <Car className="w-4 h-4 mr-2" />
                                            <span>Corporate Car</span>
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => handleNavigate("/airport-transfer")}
                                            className={`flex items-center px-3 py-2 text-sm w-full rounded-md transition ${isActive("/airport-transfer") ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-50 text-gray-600"
                                                }`}
                                        >
                                            <Plane className="w-4 h-4 mr-2" />
                                            <span>Airport Transfer</span>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </li>

                        {/* About Us */}
                        <li>
                            <button
                                onClick={() => handleNavigate("/about")}
                                className={`flex items-center px-4 py-3 w-full rounded-lg transition duration-200 ${isActive("/about") ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-100 text-gray-700"
                                    }`}
                            >
                                <Info className="w-5 h-5 mr-3" />
                                <span>About Us</span>
                            </button>
                        </li>

                        {/* Contact Us */}
                        <li>
                            <button
                                onClick={() => handleNavigate("/contact")}
                                className={`flex items-center px-4 py-3 w-full rounded-lg transition duration-200 ${isActive("/contact") ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-100 text-gray-700"
                                    }`}
                            >
                                <Info className="w-5 h-5 mr-3" />
                                <span>Contact Us</span>
                            </button>
                        </li>

                        {/* Get a Quote */}
                        <li>
                            <button
                                onClick={() => handleNavigate("/quote")}
                                className={`flex items-center px-4 py-3 w-full rounded-lg transition duration-200 ${isActive("/quote") ? "bg-green-800 text-white shadow-md" : "bg-green-900 text-white hover:shadow-lg"
                                    }`}
                            >
                                <FileText className="w-5 h-5 mr-3" />
                                <span>Get a Quote</span>
                            </button>
                        </li>
                    </ul>
                </nav>

                {/* Footer */}
                <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="text-center">
                        <h3 className="text-lg font-bold text-gray-800 mb-1.5">PVR CABS</h3>
                        <p className="text-sm text-gray-600 mb-1.5">Premium Transportation Services</p>
                        <div className="flex justify-center space-x-4">
                            <a href="tel:+917993149818" className="text-green-900 font-bold hover:text-green-700 transition-colors">
                                <span className="text-xs">📞 +91 79931 49818</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
